import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import ImageGallery from '../components/ImageGallery';

// --- 助手组件：主页上的功能卡片 ---
function FeatureCard({ title, description, gradient, onClick, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`
        rounded-lg p-4 text-left transition-all duration-300 
        hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-1 
        ${gradient}
        ${className}
      `}
    >
      <h3 className="text-white text-lg font-semibold">{title}</h3>
      <p className="text-gray-300 text-sm mt-1 whitespace-normal">{description}</p>
    </button>
  );
}

// --- 页面 1: 主页 (HomePage) ---
function HomePage({ onNavigate, demoMode }) {
  const [activeTab, setActiveTab] = useState('灵感');

  return (
    <div className="p-8 space-y-6 overflow-y-auto">
      {/* 1. 顶部欢迎语 */}
      <h1 className="text-3xl font-bold text-white">
        创世AI，让灵感快人一步！
      </h1>

      {demoMode && (
        <button onClick={() => onNavigate('demo-showcase')} className="group relative w-full overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-r from-[#12332d] via-[#152c3d] to-[#211f45] p-6 text-left shadow-xl shadow-emerald-950/20 md:p-8">
          <div className="absolute -right-12 -top-20 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl transition group-hover:bg-blue-500/30" />
          <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300"><CheckCircle2 className="h-4 w-4" /> 机车演示素材已就绪</div>
              <h2 className="text-2xl font-semibold text-white md:text-3xl">一键演示：剧本 → 分镜图 → 视频 Snap → 完整成片</h2>
              <p className="mt-3 text-sm text-gray-300">使用 14 张机车分镜和完整视频，约 16 秒展示全部 AI 生成流程。</p>
            </div>
            <span className="flex shrink-0 items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-gray-950 transition group-hover:scale-[1.02]"><Sparkles className="h-4 w-4" /> 一键生成完整演示 <ArrowRight className="h-4 w-4" /></span>
          </div>
        </button>
      )}

      {/* 5. 功能卡片 (改为可横向滚动) */}
      <div className="w-full overflow-x-auto whitespace-nowrap" style={{ scrollbarWidth: 'none', 'msOverflowStyle': 'none' }}>
        {/* 隐藏滚动条的内联样式 */}
        <style>{`.overflow-x-auto::-webkit-scrollbar { display: none; }`}</style>
        
        <div className="flex space-x-6 py-2">
          {/* 保持卡片大小 */}
          <FeatureCard
            title="AI分镜生成"
            description="零门槛上手，轻松生成影视级专业分镜"
            gradient="bg-gradient-to-br from-teal-500 to-green-600"
            onClick={() => onNavigate(demoMode ? 'demo-showcase' : 'storyboard')}
            className="w-80 md:w-96 flex-shrink-0"
          />
          <FeatureCard
            title="AI视频生成"
            description="从剧本、分镜和视频 Snap 一键生成完整成片"
            gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
            onClick={() => onNavigate('video')}
            className="w-80 md:w-96 flex-shrink-0"
          />
          <FeatureCard
            title="AI工具箱"
            description="零门槛上手，轻松生成影视级专业分镜"
            gradient="bg-gradient-to-br from-purple-500 to-pink-600"
            onClick={() => onNavigate('tools')}
            className="w-80 md:w-96 flex-shrink-0"
          />
          {/* 6. 添加 AI 漫剧卡片 */}
          <FeatureCard
            title="AI漫剧生成"
            description="一键生成分格漫画，人人都是漫画家"
            gradient="bg-gradient-to-br from-orange-500 to-red-600"
            onClick={() => onNavigate('comic')}
            className="w-80 md:w-96 flex-shrink-0"
          />
        </div>
      </div>

      {/* 3. 灵感 Tab */}
      <div className="flex space-x-4 border-b border-gray-700">
        {['灵感', '创意广告', '动画短片'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              py-2 px-1 font-medium text-sm transition-colors
              ${
                activeTab === tab
                  ? 'text-white border-b-2 border-blue-500' // 激活的tab
                  : 'text-gray-400 hover:text-white' // 未激活的tab
              }
            `}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* 4. 图片画廊 */}
      <ImageGallery />
    </div>
  );
}

export default HomePage;
